/**
 * Number and date formatting utilities for handling different formats
 */
import { logError } from '@/utils/errorHandler';

export type NumberFormat = 'dot' | 'comma';
export type DateFormat = 'MDY' | 'DMY' | 'YMD';

/**
 * Format a number according to the organization's number format preference
 */
export function formatNumberForDisplay(
  value: number,
  format: NumberFormat = 'dot',
  decimals: number = 2
): string {
  const formatted = value.toFixed(decimals);

  if (format === 'comma') {
    return formatted.replace('.', ',');
  }

  return formatted;
}

/**
 * Format number input as user types
 */
export function formatNumberInput(input: string, format: NumberFormat = 'dot'): string {
  if (!input) return input;

  const decimalSeparator = format === 'comma' ? ',' : '.';
  const otherSeparator = format === 'comma' ? '.' : ',';

  // Replace the wrong separator with the correct one
  let formatted = input.replace(new RegExp(`\\${otherSeparator}`, 'g'), decimalSeparator);

  // Remove any characters that aren't numbers, the decimal separator, or minus
  formatted = formatted.replace(new RegExp(`[^0-9\\${decimalSeparator}-]`, 'g'), '');

  // Ensure only one decimal separator
  const parts = formatted.split(decimalSeparator);
  if (parts.length > 2) {
    formatted = parts[0] + decimalSeparator + parts.slice(1).join('');
  }

  return formatted;
}

/**
 * Parse a number string that might use comma or dot as decimal separator
 */
export function parseNumberFromInput(input: string, format: NumberFormat = 'dot'): number {
  if (!input || input.trim() === '') {
    return 0;
  }

  let normalizedInput = input.trim();

  // Always convert to dot format for parsing, regardless of organization preference
  if (format === 'comma') {
    // For comma format preference, replace comma with dot
    normalizedInput = normalizedInput.replace(',', '.');
  } else {
    // For dot format preference, also replace comma with dot (cross-format support)
    normalizedInput = normalizedInput.replace(',', '.');
  }

  // Remove any non-numeric characters except dot and minus
  normalizedInput = normalizedInput.replace(/[^0-9.-]/g, '');

  const parsed = parseFloat(normalizedInput);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Validate a number input string according to the format
 */
export function isValidNumberInput(input: string, format: NumberFormat = 'dot'): boolean {
  if (!input || input.trim() === '') {
    return true; // Empty is valid
  }

  const decimalSeparator = format === 'comma' ? ',' : '.';
  const otherSeparator = format === 'comma' ? '.' : ',';

  // Check if it contains the wrong decimal separator
  if (input.includes(otherSeparator)) {
    return false;
  }

  // Check if it has more than one decimal separator
  const separatorCount = (input.match(new RegExp(`\\${decimalSeparator}`, 'g')) || []).length;
  if (separatorCount > 1) {
    return false;
  }

  // Check if it only contains numbers, the correct decimal separator, and optionally a minus sign
  const validPattern = new RegExp(`^-?\\d*\\${decimalSeparator}?\\d*$`);
  return validPattern.test(input);
}

/**
 * Format a date according to the organization's date format preference
 */
export function formatDateForDisplay(
  date: Date | string | undefined | null,
  format: DateFormat = 'MDY'
): string {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear().toString();

  switch (format) {
    case 'DMY':
      return `${day}/${month}/${year}`;
    case 'YMD':
      return `${year}-${month}-${day}`;
    case 'MDY':
    default:
      return `${month}/${day}/${year}`;
  }
}

/**
 * Get date input format string for HTML date inputs
 */
export function getDateInputFormat(_format: DateFormat = 'MDY'): string {
  // HTML date inputs always use YYYY-MM-DD format regardless of display preference
  return 'YYYY-MM-DD';
}

/**
 * Parse date string and convert to ISO format for API
 */
export function parseDateForAPI(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
}

/**
 * Get localized date format pattern for display
 */
export function getDateFormatPattern(format: DateFormat = 'MDY'): string {
  switch (format) {
    case 'DMY':
      return 'DD/MM/YYYY';
    case 'YMD':
      return 'YYYY-MM-DD';
    case 'MDY':
    default:
      return 'MM/DD/YYYY';
  }
}

/**
 * Parse a date string according to the organization's date format preference
 */
export function parseDateFromInput(input: string, format: DateFormat = 'MDY'): Date | null {
  if (!input || input.trim() === '') {
    return null;
  }

  const parts = input.trim().split(/[/-]/);
  if (parts.length !== 3) {
    return null;
  }

  let day: string, month: string, year: string;

  switch (format) {
    case 'DMY':
      [day, month, year] = parts;
      break;
    case 'YMD':
      [year, month, day] = parts;
      break;
    case 'MDY':
    default:
      [month, day, year] = parts;
      break;
  }

  const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  if (isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
}

/**
 * Get the date input format placeholder based on the organization preference
 */
export function getDateInputPlaceholder(format: DateFormat = 'MDY'): string {
  switch (format) {
    case 'DMY':
      return 'DD/MM/YYYY';
    case 'YMD':
      return 'YYYY-MM-DD';
    case 'MDY':
    default:
      return 'MM/DD/YYYY';
  }
}

/**
 * Get relative time string (e.g., "2 hours ago")
 *
 * @param dateString - ISO date string to format
 * @returns Relative time string
 */
export function getRelativeTimeString(dateString: string): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();

    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 60) {
      return rtf.format(-diffInSeconds, 'second');
    } else if (diffInMinutes < 60) {
      return rtf.format(-diffInMinutes, 'minute');
    } else if (diffInHours < 24) {
      return rtf.format(-diffInHours, 'hour');
    } else if (diffInDays < 30) {
      return rtf.format(-diffInDays, 'day');
    } else {
      // For dates older than 30 days, show formatted date
      return formatDateForDisplay(date, 'MDY'); // Use organization default
    }
  } catch (error) {
    logError(
      error instanceof Error ? error : 'Failed to format relative time',
      {
        component: 'numberFormat',
        action: 'getRelativeTimeString',
        metadata: {
          dateString,
        },
      },
      {
        showToast: false, // Silent error - formatting failure shouldn't interrupt user
        captureInSentry: true,
        userFacing: false,
      }
    );
    return dateString;
  }
}

/**
 * Format currency with organization number format preferences
 * Supports both standard formatCurrency (with Intl.NumberFormat) for dot format
 * and custom formatting for comma format
 */
export function formatCurrencyWithOrgFormat(
  amount: number | string | null | undefined,
  currency: string = 'USD',
  numberFormat: NumberFormat = 'dot'
): string {
  // Handle different input types and edge cases
  let numericAmount: number;

  if (amount === null || amount === undefined || amount === '') {
    return 'N/A';
  } else if (typeof amount === 'string') {
    const parsed = parseFloat(amount);
    numericAmount = isNaN(parsed) ? 0 : parsed;
  } else if (typeof amount === 'number') {
    numericAmount = isNaN(amount) ? 0 : amount;
  } else {
    return 'N/A';
  }

  if (numberFormat === 'comma') {
    const formattedNumber = formatNumberForDisplay(numericAmount, 'comma', 2);
    return `${formattedNumber} ${currency}`;
  } else {
    // Use standard Intl.NumberFormat for dot format
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericAmount);
    } catch (error) {
      logError(
        error instanceof Error ? error : 'Failed to format currency',
        {
          component: 'numberFormat',
          action: 'formatCurrencyWithOrgFormat',
          metadata: {
            amount,
            currency,
            numberFormat,
          },
        },
        {
          showToast: false, // Silent error - formatting failure shouldn't interrupt user
          captureInSentry: true,
          userFacing: false,
        }
      );
      // Fallback format
      return `${currency} ${formatNumberForDisplay(numericAmount, 'dot', 2)}`;
    }
  }
}

/**
 * Get the first available price from a product's prices array
 * Handles various price data structures that might come from the API
 */
export function getFirstAvailablePrice(
  prices: any[]
): { amount: number | string; currency: string } | null {
  if (!Array.isArray(prices) || prices.length === 0) {
    return null;
  }

  const firstPrice = prices[0];
  if (!firstPrice) {
    return null;
  }

  // Handle different possible price structures
  let amount: number | string;
  let currency: string;

  // Check for ProductPrice structure with amount and currency fields
  if (firstPrice.amount !== undefined && firstPrice.currency) {
    amount = firstPrice.amount;
    currency = firstPrice.currency;
  }
  // Check for price structure with amount and currency_iso
  else if (firstPrice.amount !== undefined && firstPrice.currency_iso) {
    amount = firstPrice.amount;
    currency = firstPrice.currency_iso;
  }
  // Check for legacy price structure
  else if (firstPrice.price !== undefined && firstPrice.currency) {
    amount = firstPrice.price;
    currency = firstPrice.currency;
  }
  // Fallback: try to use any numeric field and default currency
  else {
    return null;
  }

  return { amount, currency };
}

/**
 * Format a product's price display using organization preferences
 * Handles multiple price structures and fallbacks
 */
export function formatProductPrice(
  product: any,
  numberFormat: NumberFormat = 'dot',
  defaultCurrency: string = 'USD'
): string {
  // Check if product has prices array
  if (product.prices && Array.isArray(product.prices) && product.prices.length > 0) {
    const priceData = getFirstAvailablePrice(product.prices);
    if (priceData) {
      return formatCurrencyWithOrgFormat(priceData.amount, priceData.currency, numberFormat);
    }
  }

  // Check if product has a single price field (legacy format)
  if (product.price !== undefined && product.price !== null) {
    const currency = product.currency || defaultCurrency;
    return formatCurrencyWithOrgFormat(product.price, currency, numberFormat);
  }

  return 'N/A';
}
